import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Ban, History, Lock, Monitor, Shield, Users } from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { Table, TableWrapper } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { usePermission } from "@/hooks/use-permission";
import {
  fetchAdminUserDetail,
  suspendAdminUser,
  fetchUserSessions,
  fetchUserLoginHistory,
  fetchAdminRoles,
  type AdminUser,
  type AdminSession,
  type AuditEvent,
  type AdminRoleDefinition,
} from "@/lib/api";
import type { AppUser, UserRole, UserStatus } from "@/types";

function toAppUser(u: AdminUser): AppUser {
  return {
    id: u.principalId,
    name: u.displayName || u.email,
    email: u.email,
    role: (u.roleName || u.principalType || "Organization Admin") as UserRole,
    groups: u.groupNames ? u.groupNames.split(",") : [],
    mfa: u.mfaMethod || "",
    status: u.status === "ACTIVE" ? "Active" : u.status === "DEACTIVATED" ? "Suspended" : u.status === "EXPIRED" ? "Suspended" : u.status === "INVITED" ? "Invited" : "Active",
    lastActive: u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—",
  };
}

function statusVariant(status: UserStatus): "success" | "warning" | "danger" | "neutral" {
  if (status === "Active") return "success";
  if (status === "Invited") return "warning";
  if (status === "Suspended") return "danger";
  return "neutral";
}

function initials(name: string) {
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="py-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-0.5 text-sm text-ink">{value}</div>
    </div>
  );
}

function formatTimestamp(ts: string | null | undefined): string {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return "—"; }
}

const TH = "px-4 py-[9px] text-left text-[10.5px] font-semibold text-muted";
const TD = "px-4 py-[9px] text-[11.5px] text-ink";

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canSuspend = usePermission("user:suspend");
  const canEdit = usePermission("user:full");
  const [user, setUser] = useState<AppUser | null>(null);
  const [apiUser, setApiUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suspending, setSuspending] = useState(false);
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [loginHistory, setLoginHistory] = useState<AuditEvent[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [roleDef, setRoleDef] = useState<AdminRoleDefinition | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!id) { setLoading(false); return; }
    fetchAdminUserDetail(id)
      .then((result) => {
        if (cancelled) return;
        if (result.user) {
          setApiUser(result.user);
          const appUser = toAppUser(result.user);
          setUser(appUser);
          fetchAdminRoles().then((rolesResult) => {
            if (cancelled) return;
            if (!rolesResult.error) {
              const match = rolesResult.roles.find(
                (r) => r.roleName === result.user!.roleName || r.displayName === appUser.role,
              );
              if (match) setRoleDef(match);
            }
          });
        } else {
          setError(result.error || "User not found");
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Unable to connect to the admin API.");
        setLoading(false);
      });
    setSessionsLoading(true);
    fetchUserSessions(id).then((result) => {
      if (cancelled) return;
      setSessions(result.sessions);
      setSessionsLoading(false);
    });
    setHistoryLoading(true);
    fetchUserLoginHistory(id).then((result) => {
      if (cancelled) return;
      setLoginHistory(result.events);
      setHistoryLoading(false);
    });
    return () => { cancelled = true; };
  }, [id]);

  async function handleSuspend() {
    if (!user || !id) return;
    setSuspending(true);
    const result = await suspendAdminUser(id);
    if (result.ok) {
      setUser({ ...user, status: "Suspended" });
    }
    setSuspending(false);
  }

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="spinner" aria-label="Loading user" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="rounded-lg border border-line bg-surface p-8 text-center">
        <h2 className="text-lg font-bold text-ink">User not found</h2>
        <p className="mt-1 text-sm text-muted">{error || `No user exists with ID ${id}.`}</p>
        <Button className="mt-4" onClick={() => navigate("/admin/users")}>Back to Users</Button>
      </div>
    );
  }

  return (
    <div>
      <nav className="mb-5 flex items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <a href="#" onClick={(e) => e.preventDefault()} className="text-primary no-underline hover:underline">Identity</a>
        <span className="text-muted" aria-hidden>›</span>
        <Link to="/admin/users" className="text-primary no-underline hover:underline">Users</Link>
        <span className="text-muted" aria-hidden>›</span>
        <span className="font-medium text-text">{user.name}</span>
      </nav>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <Avatar initials={initials(user.name)} size={56} fontSize={20} />
          <div>
            <h1 className="text-xl font-extrabold text-ink">{user.name}</h1>
            <p className="text-sm text-muted">{user.email} · {user.id}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <Badge variant={statusVariant(user.status)}>{user.status}</Badge>
              <Badge variant="neutral" dot={false}>{user.role}</Badge>
              <Badge variant="info" dot={false}>{user.mfa || "No MFA"}</Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canSuspend && (
            <Button variant="secondary" onClick={handleSuspend} loading={suspending} disabled={user.status === "Suspended"}>
              <Ban className="mr-1.5 size-4" /> Suspend
            </Button>
          )}
          {canEdit && <Button>Edit User</Button>}
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" count={undefined}>Overview</TabsTrigger>
          <TabsTrigger value="sessions" count={sessionsLoading ? undefined : sessions.length}>Sessions</TabsTrigger>
          <TabsTrigger value="login-history" count={historyLoading ? undefined : loginHistory.length}>Login History</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5 space-y-5">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardBody>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="size-4 text-primary" /> Identity
                </CardTitle>
                <div className="mt-3 grid grid-cols-2 gap-x-4">
                  <KV label="User ID" value={user.id} />
                  <KV label="Email" value={user.email} />
                  <KV label="Role" value={user.role} />
                  <KV label="Status" value={<Badge variant={statusVariant(user.status)} size="sm">{user.status}</Badge>} />
                  <KV label="Created" value={apiUser ? formatTimestamp(apiUser.createdAt) : "—"} />
                  <KV label="Last Active" value={user.lastActive} />
                </div>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="size-4 text-primary" /> MFA & Security
                </CardTitle>
                <div className="mt-3 grid grid-cols-2 gap-x-4">
                  <KV label="MFA Method" value={user.mfa || "Not configured"} />
                  <KV label="Principal Type" value={apiUser?.principalType || "—"} />
                </div>
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardBody>
              <CardTitle className="flex items-center gap-2">
                <Users className="size-4 text-primary" /> Groups & Permissions
              </CardTitle>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div>
                  <h4 className="mb-2 text-xs font-semibold text-muted">Assigned Groups</h4>
                  {user.groups.length === 0 ? (
                    <p className="text-sm text-muted">Not assigned to any groups.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {user.groups.map((g) => (
                        <Badge key={g} variant="accent" dot={false}>{g}</Badge>
                      ))}
                    </div>
                  )}
                  <Link to="/admin/groups" className="mt-2 inline-block text-xs font-semibold text-primary hover:underline">
                    Manage groups →
                  </Link>
                </div>
                <div>
                  <h4 className="mb-2 text-xs font-semibold text-muted">Role Permissions</h4>
                  {roleDef ? (
                    <div className="space-y-1">
                      {roleDef.scopes.slice(0, 8).map((scope) => (
                        <div key={scope} className="flex items-center justify-between text-sm">
                          <span className="text-ink font-mono text-xs">{scope}</span>
                        </div>
                      ))}
                      {roleDef.scopes.length > 8 && (
                        <p className="text-xs text-muted">+{roleDef.scopes.length - 8} more scopes</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted">No role definition found.</p>
                  )}
                  <Link to="/admin/roles" className="mt-2 inline-block text-xs font-semibold text-primary hover:underline">
                    View full role catalog →
                  </Link>
                </div>
              </div>
            </CardBody>
          </Card>
        </TabsContent>

        <TabsContent value="sessions" className="mt-5">
          <SessionsTab sessions={sessions} loading={sessionsLoading} />
        </TabsContent>

        <TabsContent value="login-history" className="mt-5">
          <LoginHistoryTab events={loginHistory} loading={historyLoading} />
        </TabsContent>
      </Tabs>

      <div className="mt-6">
        <Button variant="secondary" onClick={() => navigate("/admin/users")} className="gap-1">
          <ArrowLeft className="size-4" /> Back to Users
        </Button>
      </div>
    </div>
  );
}

function SessionsTab({ sessions, loading }: { sessions: AdminSession[]; loading: boolean }) {
  return (
    <Card>
      <CardBody>
        <CardTitle className="flex items-center gap-2">
          <Monitor className="size-4 text-primary" /> Active Sessions
        </CardTitle>
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-muted">Loading sessions...</div>
        ) : (
          <TableWrapper className="mt-3">
            <Table>
              <thead>
                <tr className="border-b border-line bg-bg">
                  <th className={TH}>Session</th>
                  <th className={TH}>Auth Method</th>
                  <th className={TH}>MFA</th>
                  <th className={TH}>Assurance</th>
                  <th className={TH}>Channel</th>
                  <th className={TH}>Created</th>
                  <th className={TH}>Last Seen</th>
                  <th className={TH}>Idle Expires</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.sessionIdSuffix} className="border-b border-line last:border-b-0">
                    <td className={`${TD} font-mono`}>...{s.sessionIdSuffix}</td>
                    <td className={TD}>{s.authMethod || "—"}</td>
                    <td className={TD}>{s.mfaPresent ? "Yes" : "No"}</td>
                    <td className={TD}>{s.assuranceLevel || "—"}</td>
                    <td className={TD}>{s.channel || "—"}</td>
                    <td className={TD}>{formatTimestamp(s.createdAt)}</td>
                    <td className={TD}>{formatTimestamp(s.lastSeenAt)}</td>
                    <td className={TD}>{formatTimestamp(s.idleExpiresAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {sessions.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted">No session data available.</div>
            )}
          </TableWrapper>
        )}
      </CardBody>
    </Card>
  );
}

function OutcomePill({ outcome }: { outcome: string }) {
  const variant = outcome === "ALLOW" ? "success" : outcome === "DENY" ? "danger" : "neutral";
  return <Badge variant={variant} size="sm" dot={false}>{outcome}</Badge>;
}

function LoginHistoryTab({ events, loading }: { events: AuditEvent[]; loading: boolean }) {
  return (
    <Card>
      <CardBody>
        <CardTitle className="flex items-center gap-2">
          <History className="size-4 text-primary" /> Login History
        </CardTitle>
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-muted">Loading login history...</div>
        ) : (
          <TableWrapper className="mt-3">
            <Table>
              <thead>
                <tr className="border-b border-line bg-bg">
                  <th className={TH}>Event</th>
                  <th className={TH}>Outcome</th>
                  <th className={TH}>Auth Method</th>
                  <th className={TH}>Assurance</th>
                  <th className={TH}>MFA</th>
                  <th className={TH}>Time</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.eventId} className="border-b border-line last:border-b-0">
                    <td className={TD}>{e.eventType || "—"}</td>
                    <td className="px-4 py-[9px]"><OutcomePill outcome={e.outcome || "—"} /></td>
                    <td className={TD}>{e.authMethod || "—"}</td>
                    <td className={TD}>{e.assuranceLevel || "—"}</td>
                    <td className={TD}>{e.mfaPresent ? "Yes" : "No"}</td>
                    <td className={TD}>{formatTimestamp(e.occurredAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {events.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted">No login history available.</div>
            )}
          </TableWrapper>
        )}
      </CardBody>
    </Card>
  );
}
